// Accessory key bar for touch devices: sits directly above the on-screen keyboard and sends the
// keys phone keyboards lack. Keys fire on pointerdown (preventDefault keeps focus, so the
// keyboard stays open) and go through the focused terminal's normal onData path.
// Phones get two rows with the essentials always visible:
//   esc tab ctrl  voice  paste  ⏎  hide
//   ←  ↓  ↑  →  |  /  ~
// Voice is hold to talk (VoiceCard.tsx): release inserts, sliding off cancels.
// On the Desktop pane the same bar drives the focused Linux app through the stream bridge:
// keys become key events, paste goes through the machine clipboard, and the last key opens
// the phone keyboard (typed text is forwarded as it's typed).

import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { IconArrowDown, IconArrowLeft, IconArrowRight, IconArrowUp, IconChevronDown, IconClipboard, IconCornerDownLeft, IconKeyboard, IconMicrophone } from '@tabler/icons-react';
import { Ic, type TablerIcon } from './ds';
import { store, useStore } from '../store';
import { peekTerm } from '../term/manager';
import { mods, setCtrl, subscribeMods } from '../term/modifiers';
import { useMedia } from './mobile';
import { openSheet } from './sheets';
import { desktopKeys, desktopPaste, desktopText, type DesktopKey } from './desktopLink';
import { VoiceButton } from './VoiceCard';

type KeyId = 'esc' | 'tab' | 'ctrl' | 'enter' | 'up' | 'down' | 'left' | 'right' | 'pipe' | 'tilde' | 'slash' | 'dash' | 'tick' | 'paste' | 'hide' | 'kbd' | 'voice';

const ARROW: Partial<Record<KeyId, string>> = { up: 'A', down: 'B', right: 'C', left: 'D' };
const SYM: Partial<Record<KeyId, string>> = { pipe: '|', tilde: '~', slash: '/', dash: '-', tick: '`' };
const REPEAT_DELAY = 380;
const REPEAT_EVERY = 55;

function focusedTerm() {
  const sid = store.get().focusedSession;
  return sid === null ? undefined : peekTerm(sid);
}

function send(id: KeyId) {
  const t = focusedTerm();
  if (!t) return;
  const arrow = ARROW[id];
  if (arrow) {
    if (mods.ctrl) {
      setCtrl(false);
      t.input(`\x1b[1;5${arrow}`);
    } else {
      t.input((t.applicationCursorKeys ? '\x1bO' : '\x1b[') + arrow);
    }
    return;
  }
  if (id === 'esc' || id === 'tab' || id === 'enter') {
    setCtrl(false);
    t.input(id === 'esc' ? '\x1b' : id === 'tab' ? '\t' : '\r');
    return;
  }
  const sym = SYM[id];
  if (sym) t.input(sym); // an armed Ctrl applies in onData (Ctrl+| → ^\ etc.)
}

const DKEY: Partial<Record<KeyId, DesktopKey>> = {
  esc: { key: 'Escape', code: 'Escape' },
  tab: { key: 'Tab', code: 'Tab' },
  enter: { key: 'Enter', code: 'Enter' },
  up: { key: 'ArrowUp', code: 'ArrowUp' },
  down: { key: 'ArrowDown', code: 'ArrowDown' },
  left: { key: 'ArrowLeft', code: 'ArrowLeft' },
  right: { key: 'ArrowRight', code: 'ArrowRight' },
};

/** A key-bar key for the desktop app; an armed Ctrl applies to it. */
function sendDesktop(id: KeyId) {
  const ctrl = mods.ctrl;
  const k = DKEY[id];
  const sym = SYM[id];
  if (!k && !sym) return;
  setCtrl(false);
  if (k) desktopKeys([{ ...k, ctrl }]);
  else desktopText(sym!, ctrl);
}

async function pasteDesktop() {
  try {
    const text = await navigator.clipboard.readText();
    if (text) {
      desktopPaste(text);
      return;
    }
  } catch {
    /* fall through to the sheet */
  }
  openSheet({ kind: 'compose', sid: null });
}

/**
 * Paste the clipboard (iOS shows its small "Paste" confirmation). When the browser can't or
 * won't hand it over, or it's empty, open the compose sheet: a real text field where the
 * native long-press Paste always works.
 */
async function paste() {
  const t = focusedTerm();
  if (!t) return;
  try {
    const text = await navigator.clipboard.readText();
    if (text) {
      t.paste(text);
      return;
    }
  } catch {
    /* not allowed or unsupported: fall through to the sheet */
  }
  openSheet({ kind: 'compose', sid: t.sid });
}

const ic = (icon: TablerIcon, stroke = 1.5) => <Ic icon={icon} size={16} stroke={stroke} />;

const LABEL: Record<KeyId, { label: ReactNode; aria: string }> = {
  esc: { label: 'esc', aria: 'Escape' },
  tab: { label: 'tab', aria: 'Tab' },
  ctrl: { label: 'ctrl', aria: 'Control (applies to the next key)' },
  enter: { label: ic(IconCornerDownLeft, 1.8), aria: 'Enter' },
  up: { label: ic(IconArrowUp), aria: 'Arrow up' },
  down: { label: ic(IconArrowDown), aria: 'Arrow down' },
  left: { label: ic(IconArrowLeft), aria: 'Arrow left' },
  right: { label: ic(IconArrowRight), aria: 'Arrow right' },
  pipe: { label: '|', aria: 'Pipe' },
  tilde: { label: '~', aria: 'Tilde' },
  slash: { label: '/', aria: 'Slash' },
  dash: { label: '-', aria: 'Dash' },
  tick: { label: '`', aria: 'Backtick' },
  paste: { label: ic(IconClipboard), aria: 'Paste' },
  hide: { label: ic(IconChevronDown), aria: 'Hide keyboard' },
  voice: { label: ic(IconMicrophone), aria: 'Voice' },
  kbd: { label: ic(IconKeyboard), aria: 'Keyboard' },
};

const WIDE: KeyId[][] = [['esc', 'tab', 'ctrl', 'left', 'down', 'up', 'right', 'pipe', 'slash', 'tilde', 'dash', 'tick', 'voice', 'paste', 'enter', 'hide']];
const PHONE: KeyId[][] = [
  ['esc', 'tab', 'ctrl', 'voice', 'paste', 'enter', 'hide'],
  ['left', 'down', 'up', 'right', 'pipe', 'slash', 'tilde'],
];
/** The desktop's bar: the keyboard toggle instead of hide, and no voice (it talks to sessions). */
const forDesktop = (rows: KeyId[][]): KeyId[][] => rows.map((r) => r.filter((k) => k !== 'voice').map((k) => (k === 'hide' ? 'kbd' : k)));

/**
 * Phone keyboard for the desktop: a hidden input in this page (a tap here may raise the
 * keyboard; focusing into the cross-origin stream frame can't) whose input is forwarded.
 */
function useDesktopTyping(enabled: boolean) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const onBefore = (e: InputEvent) => {
      e.preventDefault();
      const ctrl = mods.ctrl;
      switch (e.inputType) {
        case 'insertText':
        case 'insertReplacementText':
        case 'insertCompositionText':
          if (e.data) {
            setCtrl(false);
            desktopText(e.data, ctrl);
          }
          break;
        case 'insertLineBreak':
        case 'insertParagraph':
          desktopKeys([{ key: 'Enter', code: 'Enter' }]);
          break;
        case 'deleteContentBackward':
          desktopKeys([{ key: 'Backspace', code: 'Backspace', ctrl }]);
          setCtrl(false);
          break;
        case 'insertFromPaste':
          if (e.dataTransfer?.getData('text/plain')) desktopPaste(e.dataTransfer.getData('text/plain'));
          break;
      }
    };
    el.addEventListener('beforeinput', onBefore as EventListener);
    return () => el.removeEventListener('beforeinput', onBefore as EventListener);
  }, [enabled]);
  return ref;
}

export function KeyBar({ target = 'term' }: { target?: 'term' | 'desktop' }) {
  const desk = target === 'desktop';
  const typeRef = useDesktopTyping(desk);
  const wide = useMedia('(min-width: 600px)');
  const ctrl = useSyncExternalStore(subscribeMods, () => mods.ctrl);
  const sid = useStore((s) => s.focusedSession);
  const repeat = useRef<{ t: ReturnType<typeof setTimeout> | null; i: ReturnType<typeof setInterval> | null }>({ t: null, i: null });

  const stop = () => {
    if (repeat.current.t) clearTimeout(repeat.current.t);
    if (repeat.current.i) clearInterval(repeat.current.i);
    repeat.current = { t: null, i: null };
  };
  useEffect(() => stop, []);
  // Disarm a pending Ctrl when the bar goes away (terminal lost focus).
  useEffect(() => () => setCtrl(false), []);

  const down = (id: KeyId) => {
    switch (id) {
      case 'ctrl':
        setCtrl(!mods.ctrl);
        return;
      case 'hide':
        setCtrl(false);
        focusedTerm()?.blur();
        return;
      case 'paste':
      case 'kbd':
        return; // need user activation: handled on click (pointerup)
    }
    const fire = desk ? sendDesktop : send;
    fire(id);
    if (ARROW[id]) {
      stop();
      repeat.current.t = setTimeout(() => {
        repeat.current.i = setInterval(() => fire(id), REPEAT_EVERY);
      }, REPEAT_DELAY);
    }
  };

  const toggleKeyboard = () => {
    const el = typeRef.current;
    if (!el) return;
    if (document.activeElement === el) el.blur();
    else el.focus();
  };

  const rows = desk ? forDesktop(wide ? WIDE : PHONE) : wide ? WIDE : PHONE;

  const key = (id: KeyId) => (
    <button
      key={id}
      type="button"
      tabIndex={-1}
      className={`kb-key kb-${id} ${id === 'ctrl' && ctrl ? 'is-armed' : ''}`}
      aria-label={LABEL[id].aria}
      aria-pressed={id === 'ctrl' ? ctrl : undefined}
      data-testid={`key-${id}`}
      data-armed={id === 'ctrl' ? (ctrl ? '1' : '0') : undefined}
      onPointerDown={(e) => {
        e.preventDefault(); // keep focus (and the on-screen keyboard) on the terminal
        if (e.button === 0) down(id);
      }}
      onMouseDown={(e) => e.preventDefault()}
      onPointerUp={stop}
      onPointerCancel={stop}
      onPointerLeave={stop}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        if (id === 'paste') void (desk ? pasteDesktop() : paste());
        if (id === 'kbd') toggleKeyboard();
      }}
    >
      {LABEL[id].label}
    </button>
  );

  return (
    <div className="keybar" role="toolbar" aria-label={desk ? 'Desktop keys' : 'Terminal keys'} data-testid="keybar" data-target={target} data-rows={rows.length}>
      {desk && (
        <input
          ref={typeRef}
          className="kb-type"
          type="text"
          aria-label="Type into the desktop"
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="enter"
          data-testid="desktop-type"
        />
      )}
      {rows.map((r, i) => (
        <div className="kb-row" key={i}>
          {r.map((id) =>
            id !== 'voice' ? (
              key(id)
            ) : sid !== null ? (
              <VoiceButton key={id} sid={sid} className="kb-key kb-voice" testid="key-voice">
                {LABEL.voice.label}
              </VoiceButton>
            ) : null,
          )}
        </div>
      ))}
    </div>
  );
}
